import React, { Children, useState } from "react";
import {
	ScanFace,
	RotateCcw,
	Sparkles,
	Loader2,
	Mars,
	Venus,
	NonBinary,
	VenusAndMars,
	Transgender,
	MarsStroke,
	Snowflake,
	Leaf,
	Sun,
	Flower,
	TentTree,
	BriefcaseBusiness,
	House,
	BusFront,
	Download,
} from "lucide-react";
import { GoogleGenAI } from "@google/genai";
import { toastSuccess } from "../../../utility/toast";
import toast from "react-hot-toast";
import MbtiSelector from "./mbtiSelector";
import ColorPicker from "./colorPicker";
import SpringBtn from "../../common/springBtn";

// 生成动态 prompt
const generatePrompt = (mbti, colors, gender, season, scene) => {
	return `【最高指令：高保真面部融合】 绝对优先： 严格基于用户上传图片的面部特征，高保真地将其融合到生成的时尚模特上。确保生成的模特面部与原图高度相似，五官、神态和脸型轮廓必须清晰可辨。
【核心风格指令：活力色彩街区 (Vibrant Color Block Style)】 请严格遵循以下源自参考图的搭配法则，生成一套充满活力和视觉冲击力的造型。这是造型的灵魂。
大胆色彩组合 (Bold Color Combinations): 这是最重要的规则。整个造型必须围绕用户调色盘中 2到3个高饱和度、高明度的颜色 进行构建。色彩组合要大胆、直接，例如参考图中的 橙+粉、红+黄、红+天蓝、粉+绿。
大色块叙事 (Large Color Block Narrative): 穿搭应由大面积的纯色单品构成，以此来强化色彩的力量感。避免使用琐碎的图案或印花，让色块本身成为主角。
简约廓形，色彩至上 (Simple Silhouettes, Color is Supreme): 服装的剪裁和款式应相对简约，以更好地承载和凸显色彩。例如：宽松的阔腿裤、oversize的西装或衬衫、简约的吊带裙、基础款针织衫等。
配件的色彩游戏 (Accessory Color Play): 配饰（包、鞋、帽子、墨镜）是完成造型的关键。它们必须遵循以下两种策略之一：
色彩呼应： 配饰的颜色与服装的某个主色块完全一致，以增强整体感。
色彩点睛： 配饰作为第三种或第四种跳色出现，增加造型的趣味性和层次感。
 
【穿搭定制参数 (Customization Parameters)】
MBTI 特征融入: 将 ${mbti || "enfj"} 的核心性格，融入模特的神态、姿态和整体能量感中。例如：
外向型 (E*):** 姿态更开放、动态，表情可能带有微笑或自信的大笑。
内向型 (I*):** 姿态可能更沉静，但眼神坚定，通过一种酷感来驾驭鲜艳的色彩。
主色盘 (Main Color Palette): ${colors.join(", ")} (这是生成此风格的关键输入)
性别 (Gender): ${gender}
季节 (Season): ${season}
穿搭场景 (Occasion/Scene): ${scene}
 
【图像风格与质量要求 (Image Style & Quality)】
整体风格: 明亮、充满活力的时尚街拍或品牌造型图册 (Lookbook) 质感，与参考图的风格保持一致。
模特: 自信、阳光、富有表现力，能够轻松驾驭大胆的色彩。
背景 (Fixed): 简洁的纯色背景（如亮白色、米白色或一个非常淡的彩色），模拟时尚杂志或品牌Lookbook中的抠图效果。这能确保人物和服装色彩成为画面的绝对焦点。
光线与画质: 4K分辨率。光线必须明亮、均匀，且带有温暖感，仿佛地中海的阳光，以最大限度地展现色彩的真实饱和度。`;
};

function Dashboard({
	selectedMbti,
	setSelectedMbti,
	generatedImage,
	setGeneratedImage,
	bolb,
	isGenerating,
	setIsGenerating,
}) {
	const [selectedColors, setSelectedColors] = useState([
		"#FF0080",
		"#FF8C00",
		"#40E0D0",
	]);
	const [selectedImage, setSelectedImage] = useState(null);
	const [selectedGender, setSelectedGender] = useState("Female");
	const [selectedSeason, setSelectedSeason] = useState("Spring");
	const [selectedScene, setSelectedScene] = useState("Holiday");
	const [apiKey, setApiKey] = useState("");
	const selector = React.useRef(null);

	// 处理图片选择
	const handleImageChange = (event) => {
		const file = event.target.files[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				setSelectedImage(e.target.result);
			};
			reader.readAsDataURL(file);
		}
	};

	// 调用 Google Gemini 生成图片
	async function generateImage() {
		setIsGenerating(true);

		try {
			console.log("开始生成图片...");

			// 初始化 GoogleGenAI 客户端
			const ai = new GoogleGenAI({
				apiKey: apiKey,
			});

			// 生成动态 prompt
			const dynamicPrompt = generatePrompt(
				selectedMbti,
				selectedColors,
				selectedGender,
				selectedSeason,
				selectedScene
			);

			console.log("生成的 prompt:", dynamicPrompt);

			// 准备图片数据
			let imageData = null;
			let mimeType = "image/png";

			if (selectedImage) {
				// 使用用户上传的图片
				const response = await fetch(selectedImage);
				const blob = await response.blob();
				const arrayBuffer = await blob.arrayBuffer();
				imageData = new Uint8Array(arrayBuffer);
				mimeType = blob.type || "image/png";
			} else {
				// 使用默认展位图
				const defaultImagePath = require("../../../asset/img/example_canvas.jpeg");
				const response = await fetch(defaultImagePath);
				const blob = await response.blob();
				const arrayBuffer = await blob.arrayBuffer();
				imageData = new Uint8Array(arrayBuffer);
				mimeType = "image/jpeg";
			}

			// 将图片数据转换为 base64 - 使用 FileReader 方法
			const base64Image = await new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					// 移除 data:image/...;base64, 前缀
					const base64 = reader.result.split(",")[1];
					resolve(base64);
				};
				reader.onerror = reject;
				reader.readAsDataURL(new Blob([imageData], { type: mimeType }));
			});

			// 按照官方示例构建 prompt
			const prompt = [
				{ text: dynamicPrompt },
				{
					inlineData: {
						mimeType: mimeType,
						data: base64Image,
					},
				},
			];

			const response = await ai.models.generateContent({
				model: "gemini-2.5-flash-image",
				contents: prompt,
				config: {
					generationConfig: {
						responseModalities: ["IMAGE"],
					},
					imageConfig: {
						aspectRatio: "9:16",
					},
				},
			});

			console.log("Gemini response:", response);

			// 从响应中提取图片数据
			if (
				response.candidates &&
				response.candidates[0] &&
				response.candidates[0].content &&
				response.candidates[0].content.parts
			) {
				const parts = response.candidates[0].content.parts;
				for (const part of parts) {
					if (part.inlineData && part.inlineData.data) {
						// 将 base64 数据转换为 blob
						const imageData = part.inlineData.data;
						const byteCharacters = atob(imageData);
						const byteNumbers = new Array(byteCharacters.length);
						for (let i = 0; i < byteCharacters.length; i++) {
							byteNumbers[i] = byteCharacters.charCodeAt(i);
						}
						const byteArray = new Uint8Array(byteNumbers);
						const blob = new Blob([byteArray], { type: "image/png" });
						const imageBlob = URL.createObjectURL(blob);

						setGeneratedImage(imageBlob);
						toastSuccess("Image generated successfully!");
						return;
					}
				}
			}

			// 如果没有找到图片数据，使用占位符
			const imageUrl =
				"https://via.placeholder.com/800x600/4CAF50/FFFFFF?text=Fashion+Generated";
			const imageResponse = await fetch(imageUrl);
			const blob = await imageResponse.blob();
			const imageBlob = URL.createObjectURL(blob);

			setGeneratedImage(imageBlob);
			toastSuccess("Image generated successfully!");
		} catch (error) {
			console.error("Error generating image:", error);
			toast.error("Failed to generate image.");
		} finally {
			setIsGenerating(false);
		}
	}

	// Download
	function download() {
		let downloadUrl = null;
		let filename = "";

		if (generatedImage) {
			// 下载生成的图片
			downloadUrl = generatedImage;
			filename = "nano_banana_generated.jpg";
		} else {
			// 下载默认展位图
			downloadUrl = require("../../../asset/img/example_canvas.jpeg");
			filename = "example_canvas.jpeg";
		}

		if (downloadUrl) {
			let elem = document.createElement("a");
			elem.setAttribute("href", downloadUrl);
			elem.setAttribute("download", filename);
			document.body.appendChild(elem);
			elem.click();
			document.body.removeChild(elem);
			toastSuccess("Download started");
		}
	}

	function reset() {
		setSelectedMbti("enfj");
		setGeneratedImage(null);
		setSelectedImage(null);
		setSelectedColors(["#FF0080", "#FF8C00", "#40E0D0"]);
		setSelectedGender("Female");
		setSelectedSeason("Spring");
		setSelectedScene("Holiday");
		setApiKey("");
	}

	return (
		<div className="w-full p-6 md:p-0 md:w-1/2 lg:w-2/5 flex gap-8 flex-col justify-between">
			<div className="space-y-4 overflow-y-auto">
				{/* Color picker */}
				<ColorPicker
					colors={selectedColors}
					onColorsChange={setSelectedColors}
				/>

				<div className="px-6 py-4 bg-gray-50 rounded-2xl flex justify-between items-stretch space-x-4">
					{/* Avatar Section */}
					<div className="flex items-center">
						{/* Avatar Circle - 动态高度，保持正方形 */}
						<div className="relative h-full aspect-square group">
							<div className="absolute inset-0 w-full h-full rounded-xl overflow-hidden bg-gray-900/20 backdrop-blur-sm flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 cursor-pointer z-10 transition-opacity">
								<ScanFace className="h-12 w-12 stroke-[1.5px] text-white" />
								<p className="text-sm text-white font-light">Swap face</p>
							</div>
							{selectedImage ? (
								<div
									className="w-full h-full rounded-xl overflow-hidden bg-cover bg-center bg-no-repeat"
									style={{ backgroundImage: `url(${selectedImage})` }}></div>
							) : (
								<div
									className="w-full h-full rounded-2xl overflow-hidden bg-cover bg-center bg-no-repeat cursor-pointer hover:opacity-80 transition-opacity"
									style={{
										backgroundImage: `url(${require("../../../asset/img/example_avatar.jpeg")})`,
									}}></div>
							)}

							{/* Hidden file input */}
							<input
								ref={selector}
								type="file"
								accept=".jpg,.jpeg,.heic,.heif,.webp,.png"
								className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
								onChange={handleImageChange}
							/>
						</div>
					</div>

					{/* MBTI Selector Section */}
					<div className="flex items-center space-x-4">
						{/* MBTI Slide Selector */}
						<MbtiSelector
							mbti={selectedMbti}
							onSelect={(mbti) => {
								setSelectedMbti(mbti);
							}}
						/>
					</div>
				</div>

				{/* Options */}
				<div className="space-y-1">
					<div className="px-6 py-4 bg-gray-50 rounded-t-2xl flex justify-between items-center">
						<h1>Gender</h1>
						<div className="space-x-2">
							{Children.toArray(
								[
									"Male",
									"Female",
									"Non-binary",
									"Transgender",
									"Mars-stroke",
									"Venus-and-Mars",
								].map((i, index) => (
									<SpringBtn
										key={index}
										tooltip={i}
										onClick={() => setSelectedGender(i)}
										className={`h-12 w-12 text-center rounded-xl transition ${
											selectedGender === i
												? "bg-gray-800 text-white"
												: "text-gray-800 bg-white hover:bg-gray-100"
										}`}>
										<span>
											{i === "Male" ? (
												<Mars className="inline-block w-5 h-5" />
											) : i === "Female" ? (
												<Venus className="inline-block w-5 h-5" />
											) : i === "Non-binary" ? (
												<NonBinary className="inline-block w-5 h-5" />
											) : i === "Transgender" ? (
												<Transgender className="inline-block w-5 h-5" />
											) : i === "Mars-stroke" ? (
												<MarsStroke className="inline-block w-5 h-5" />
											) : (
												<VenusAndMars className="inline-block w-5 h-5" />
											)}
										</span>
									</SpringBtn>
								))
							)}
						</div>
					</div>
					<div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
						<h1>Season</h1>
						<div className="space-x-2">
							{Children.toArray(
								["Spring", "Summer", "Autumn", "Winter"].map((i, index) => (
									<SpringBtn
										key={index}
										tooltip={i}
										onClick={() => setSelectedSeason(i)}
										className={`h-12 w-12 text-center rounded-xl transition ${
											selectedSeason === i
												? "bg-gray-800 text-white"
												: "text-gray-800 bg-white hover:bg-gray-100"
										}`}>
										<span>
											{i === "Spring" ? (
												<Flower className="inline-block w-5 h-5" />
											) : i === "Summer" ? (
												<Sun className="inline-block w-5 h-5" />
											) : i === "Autumn" ? (
												<Leaf className="inline-block w-5 h-5" />
											) : (
												<Snowflake className="inline-block w-5 h-5" />
											)}
										</span>
									</SpringBtn>
								))
							)}
						</div>
					</div>
					<div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-between items-center">
						<h1>Scene</h1>
						<div className="space-x-2">
							{Children.toArray(
								["Holiday", "Street", "Home", "Office"].map((i, index) => (
									<SpringBtn
										key={index}
										tooltip={i}
										onClick={() => setSelectedScene(i)}
										className={`w-12 h-12 text-center rounded-xl transition ${
											selectedScene === i
												? "bg-gray-800 text-white"
												: "text-gray-800 bg-white hover:bg-gray-100"
										}`}>
										<span>
											{i === "Holiday" ? (
												<TentTree className="inline-block w-5 h-5" />
											) : i === "Street" ? (
												<BusFront className="inline-block w-5 h-5" />
											) : i === "Home" ? (
												<House className="inline-block w-5 h-5" />
											) : (
												<BriefcaseBusiness className="inline-block w-5 h-5" />
											)}
										</span>
									</SpringBtn>
								))
							)}
						</div>
					</div>
				</div>

				<div className="px-6 py-4 bg-gray-50 rounded-2xl flex justify-between items-center">
					<h1>Gemini API Key</h1>
						<input
							type="password"
							value={apiKey}
							onChange={(e) => setApiKey(e.target.value)}
							placeholder="Your own secret key"
							className="w-1/2 whitespace-nowrap bg-white p-2 rounded-xl text-sm text-center placeholder:font-light"
						/>
				</div>
			</div>

			{/* Button */}
			<div className="flex justify-between gap-4">
				{/* Reset */}
				<SpringBtn
					onClick={() => reset()}
					className="block group py-4 px-6 text-gray-800 hover:text-white leading-8 rounded-2xl overflow-hidden bg-gray-50 hover:bg-rose-500 transition">
					<RotateCcw className="w-5 h-5 transition duration-400 group-hover:rotate-[-360deg]" />
				</SpringBtn>
				<div className="flex gap-4">
					{/* Download */}
					<SpringBtn
						onClick={() => download()}
						className="block group py-4 px-6 text-gray-800 hover:text-white leading-8 rounded-2xl overflow-hidden bg-gray-50 hover:bg-gray-800 transition">
						<Download className="w-5 h-5 transition duration-100 " />
					</SpringBtn>
					{/* Generate & Download */}
					<SpringBtn
						disabled={isGenerating}
						onClick={() => generateImage()}
						className="block group py-4 px-8 bg-gray-800 text-white hover:text-white text-lg leading-8 rounded-2xl overflow-hidden transition disabled:opacity-50 disabled:cursor-not-allowed">
						{isGenerating ? (
							<Loader2 className="inline-block animate-spin w-5 h-5 mr-2" />
						) : (
							<Sparkles className="inline-block w-5 h-5 mr-2" />
						)}
						{isGenerating ? "Generating..." : "Remix"}
					</SpringBtn>
				</div>
			</div>
		</div>
	);
}

export default Dashboard;
